import { Component, Output, EventEmitter, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';

// Export FilterOptions interface
export interface FilterOptions {
  searchText: string;
  priority: string;
  assignee: string;
  labels: string[];
  dueDateRange: {
    start: Date | null;
    end: Date | null;
  };
}

@Component({
  selector: 'app-task-search-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
  ],
  templateUrl: './task-search-filter.component.html',
  styleUrls: ['./task-search-filter.component.scss'],
})
export class TaskSearchFilterComponent implements OnInit {
  @Input() filterOptions!: FilterOptions;

  /** true = search panel (single input), false = filter panel (original) */
  @Input() searchMode = false;

  /** List of assignee names passed in from the parent (from app-users API) */
  private _allAssignees: string[] = [];
  @Input() set assignees(names: string[]) {
    this._allAssignees = names;
    this.assigneeOptions = ['all', ...names];
  }
  @Output() filterChange = new EventEmitter<FilterOptions>();

  priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
  ];

  assigneeOptions: string[] = ['all'];
  labelOptions: string[] = [];

  // Suggestion state (search mode only)
  suggestions: string[] = [];
  showSuggestions = false;

  ngOnInit() {}

  // ── Search mode methods ─────────────────────────────────────────────────

  onSearchInput(value: string): void {
    const q = value.trim().toLowerCase();
    // Show assignee suggestions when input matches any name
    this.suggestions = q
      ? this._allAssignees.filter((n) => n.toLowerCase().includes(q))
      : [];
    this.showSuggestions = this.suggestions.length > 0;
    this.filterChange.emit(this.filterOptions);
  }

  onSearchFocus(): void {
    const q = (this.filterOptions.searchText || '').trim().toLowerCase();
    this.suggestions = q
      ? this._allAssignees.filter((n) => n.toLowerCase().includes(q))
      : [];
    this.showSuggestions = this.suggestions.length > 0;
  }

  onSearchBlur(): void {
    setTimeout(() => (this.showSuggestions = false), 150);
  }

  /** User clicked a suggestion — fill input with that name so assignee search runs */
  pickSuggestion(name: string): void {
    this.filterOptions.searchText = name;
    this.showSuggestions = false;
    this.filterChange.emit(this.filterOptions);
  }

  clearSearch(): void {
    this.filterOptions.searchText = '';
    this.suggestions = [];
    this.showSuggestions = false;
    this.filterChange.emit(this.filterOptions);
  }

  // ── Filter mode methods (original, untouched) ───────────────────────────

  onFilterChange() {
    this.filterChange.emit(this.filterOptions);
  }

  clearFilters() {
    this.filterOptions = {
      searchText: '',
      priority: 'all',
      assignee: 'all',
      labels: [],
      dueDateRange: {
        start: null,
        end: null,
      },
    };
    this.filterChange.emit(this.filterOptions);
  }

  removeLabel(label: string) {
    this.filterOptions.labels = this.filterOptions.labels.filter(
      (l) => l !== label,
    );
    this.onFilterChange();
  }

  hasActiveFilters(): boolean {
    return !(
      !this.filterOptions.searchText &&
      this.filterOptions.priority === 'all' &&
      this.filterOptions.assignee === 'all' &&
      this.filterOptions.labels.length === 0 &&
      !this.filterOptions.dueDateRange.start &&
      !this.filterOptions.dueDateRange.end
    );
  }

  getPriorityLabel(priority: string): string {
    const labels: { [key: string]: string } = {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      critical: 'Critical',
      all: 'All',
    };
    return labels[priority] || priority;
  }
}
